'use client'

import * as React from 'react'
import { useFormContext, Controller } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Search } from 'lucide-react'

import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select'
import type { TranslationFormData } from './validation'
import { LanguageCode, LANGUAGES } from '@/data/languages'

interface NestedMessages {
    [key: string]: string | NestedMessages
}

/**
 * Translation form component
 * Handles translation fields using React Hook Form with Controller
 * Supports nested messages with search functionality
 */
export const TranslationForm = () => {
    const { t } = useTranslation(['languages', 'common'])
    const {
        control,
        register,
        watch,
        setValue,
        formState: { errors },
    } = useFormContext<TranslationFormData>()

    const [searchQuery, setSearchQuery] = React.useState('')
    const [searchMode, setSearchMode] = React.useState<'both' | 'key' | 'value'>('both')

    const locale = watch('locale')
    const messages = watch('messages')

    const selectedLanguage = React.useMemo(() => {
        if (!locale) return null
        const langCode = locale.split('-')[0] as LanguageCode
        return LANGUAGES[langCode] || null
    }, [locale])

    const filterMessages = React.useCallback(
        (messages: NestedMessages, query: string): NestedMessages => {
            if (!query) return messages

            const filtered: NestedMessages = {}
            const lowerQuery = query.toLowerCase()

            Object.entries(messages).forEach(([key, value]) => {
                if (typeof value === 'object') {
                    const filteredNested = filterMessages(value, query)
                    if (Object.keys(filteredNested).length > 0) {
                        filtered[key] = filteredNested
                    }
                } else {
                    const matchesKey = searchMode !== 'value' && key.toLowerCase().includes(lowerQuery)
                    const matchesValue = searchMode !== 'key' && value.toLowerCase().includes(lowerQuery)

                    if (matchesKey || matchesValue) {
                        filtered[key] = value
                    }
                }
            })

            return filtered
        },
        [searchMode],
    )

    const renderNestedInputs = React.useCallback(
        (messages: NestedMessages, parentKey = '') => {
            const filteredMessages = searchQuery ? filterMessages(messages, searchQuery) : messages

            if (Object.keys(filteredMessages).length === 0) {
                return (
                    <div className="text-sm italic text-muted-foreground">
                        {t('form.noTranslationsFound', { query: searchQuery })}
                    </div>
                )
            }

            return Object.entries(filteredMessages).map(([key, value]) => {
                const fullKey = parentKey ? `${parentKey}.${key}` : key
                const fieldName = `messages.${fullKey}` as const

                if (typeof value === 'object') {
                    return (
                        <div key={fullKey} className="mt-4 space-y-2">
                            <div className="text-sm font-medium">
                                {key.charAt(0).toUpperCase() + key.slice(1)}
                            </div>
                            <div className="border-l border-border pl-4">
                                {renderNestedInputs(value, fullKey)}
                            </div>
                        </div>
                    )
                }

                const isKeyMatch = searchQuery && key.toLowerCase().includes(searchQuery.toLowerCase())
                const isValueMatch = searchQuery && value.toLowerCase().includes(searchQuery.toLowerCase())

                return (
                    <div key={fullKey} className="mt-2">
                        <Label
                            htmlFor={fieldName}
                            className={`block text-sm font-medium ${isKeyMatch ? 'text-blue-600' : 'text-foreground'
                                }`}
                        >
                            {key}
                        </Label>
                        <Input
                            id={fieldName}
                            {...register(fieldName)}
                            defaultValue={value}
                            className={`mt-1 w-full ${isValueMatch ? 'border-blue-500' : ''}`}
                        />
                        {errors.messages && (errors.messages as any)[fullKey] && (
                            <p className="mt-1 text-xs leading-tight text-red-600">
                                {(errors.messages as any)[fullKey]?.message as string}
                            </p>
                        )}
                    </div>
                )
            })
        },
        [register, errors.messages, searchQuery, filterMessages, t],
    )

    if (!selectedLanguage) {
        return (
            <div className="text-muted-foreground text-center py-8">
                {t('form.selectLanguage')}
            </div>
        )
    }

    return (
        <div className="space-y-6">
            <Card>
                <CardHeader>
                    <CardTitle>
                        {selectedLanguage.name} {t('form.translations')}
                    </CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                    <Controller
                        name="enabled"
                        control={control}
                        render={({ field }) => (
                            <div className="flex items-center space-x-2">
                                <Switch
                                    id="enabled"
                                    checked={field.value ?? true}
                                    onCheckedChange={field.onChange}
                                />
                                <Label htmlFor="enabled">{t('form.enableTranslation')}</Label>
                            </div>
                        )}
                    />

                    <div className="space-y-4">
                        <div className="space-y-2">
                            <Label htmlFor="search" className="block text-sm font-medium">
                                {t('form.searchTranslations')}
                            </Label>
                            <div className="flex gap-2">
                                <div className="relative flex-1">
                                    <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                                    <Input
                                        id="search"
                                        type="search"
                                        placeholder={t('form.searchPlaceholder')}
                                        value={searchQuery}
                                        onChange={(e) => setSearchQuery(e.target.value)}
                                        className="pl-9"
                                    />
                                </div>
                                <Select
                                    value={searchMode}
                                    onValueChange={(value) => setSearchMode(value as 'both' | 'key' | 'value')}
                                >
                                    <SelectTrigger className="w-[180px]">
                                        <SelectValue placeholder={t('form.searchMode')} />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="both">{t('form.searchBoth')}</SelectItem>
                                        <SelectItem value="key">{t('form.searchKeys')}</SelectItem>
                                        <SelectItem value="value">{t('form.searchValues')}</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                            <p className="text-xs text-muted-foreground">
                                {t('form.searchHint', {
                                    mode:
                                        searchMode === 'both'
                                            ? t('form.searchBoth')
                                            : searchMode === 'key'
                                                ? t('form.searchKeys')
                                                : t('form.searchValues'),
                                })}
                            </p>
                        </div>

                        <div className="rounded-lg border bg-card p-4">
                            {renderNestedInputs((messages || {}) as NestedMessages)}
                        </div>
                    </div>
                </CardContent>
            </Card>
        </div>
    )
}

